/**
 * The add-to-cart button's own feedback: "Adding…", then a tick and "Added",
 * then back to its original label after four seconds.
 *
 * This is product-page chrome rather than cart state, so it is kept apart from
 * `cart-add-forms.ts` — nothing here knows what a cart is, and the labels come
 * from `window.theme.strings`, set by `layout/theme.liquid`.
 *
 * The original label is stashed in a WeakMap rather than a data attribute
 * because it is markup (an icon plus text) and the button is often replaced
 * wholesale by a section render, which should take its stash with it.
 */

const LABEL_STORE = new WeakMap<HTMLElement, string>();

/** How long "Added" stays up before the button returns to its label. */
const ADDED_MS = 4000;

function strings(): Record<string, string> {
  return window.theme?.strings ?? {};
}

function tickIcon(): string {
  return window.theme?.icons?.tick ?? "";
}

function storeLabel(button: HTMLElement): void {
  if (!LABEL_STORE.has(button)) LABEL_STORE.set(button, button.innerHTML);
}

function restoreLabel(button: HTMLElement): void {
  const label = LABEL_STORE.get(button);
  if (label !== undefined) button.innerHTML = label;
}

export function setAdding(button: HTMLElement | null): void {
  if (!button) return;
  storeLabel(button);
  button.setAttribute("disabled", "disabled");
  button.classList.add("confirmation", "adding");
  button.innerHTML = strings().productAddingToCart ?? "Adding…";
}

export function setAdded(button: HTMLElement | null): void {
  if (!button) return;
  button.classList.add("added");
  button.innerHTML = `${tickIcon()} ${strings().productAddedToCart ?? "Added"}`;

  window.setTimeout(() => {
    button.removeAttribute("disabled");
    button.classList.remove("confirmation", "adding", "added");
    restoreLabel(button);
  }, ADDED_MS);
}

export function setFailed(button: HTMLElement | null): void {
  if (!button) return;
  button.removeAttribute("disabled");
  button.classList.remove("confirmation", "adding", "added");
  restoreLabel(button);
}

/**
 * The buttons that should show the state of one add: the form's own submit, and
 * on small screens the sticky bar that duplicates it outside the form.
 */
export function buttonsFor(form: HTMLFormElement): (HTMLElement | null)[] {
  const submit = form.querySelector<HTMLElement>('[type="submit"]');
  const sticky = window.matchMedia("(max-width: 767px)").matches
    ? document.querySelector<HTMLElement>(
        ".product-area__add-to-cart-xs button",
      )
    : null;

  return [submit, sticky];
}
